import { withDependencies } from '@wix/thunderbolt-ioc'
import type { Experiments, IAppWillMountHandler, IHeadContent } from '@wix/thunderbolt-symbols'
import { ExperimentsSymbol, HeadContentSymbol } from '@wix/thunderbolt-symbols'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

export const PREVENT_ANCHOR_RELOAD_SPEC = 'specs.thunderbolt.preventAnchorReloadBeforeHydration' as const

// Both functions below are serialized (via `.toString()`) into the inline SSR `<script>`, so
// they must stay self-contained (no closures over module scope, no runtime imports) and keep
// their bodies comment-free — anything inside the body ships inline; explain above the function.

// True only for a same-page link: an `a[data-anchor]` with an href that resolves to the current
// origin + pathname (query + hash ignored). Cross-page / cross-origin / new-tab links return false.
export function isSamePageAnchor(e: MouseEvent): boolean {
	const target = e.target as HTMLElement | null
	const anchor = target && target.closest && target.closest('a[data-anchor]')
	if (!anchor || anchor.getAttribute('target') === '_blank') {
		return false
	}
	const href = anchor.getAttribute('href')
	if (!href) {
		return false
	}
	try {
		const url = new URL(href, location.href)
		return url.origin === location.origin && url.pathname === location.pathname
	} catch (_) {
		return false
	}
}

export function preventSamePageAnchorReloadBeforeHydration(e: MouseEvent) {
	if (e.metaKey || e.ctrlKey) {
		return
	}
	if (isSamePageAnchor(e)) {
		e.preventDefault()
	}
}

// Inline `<script>` injected into the SSR `<head>`: installs the guard as a single capture-phase
// `click` listener on `document`, exposed as `window.__tbAnchorGuard` so `ClickHandlerRegistrar`
// can remove it once `onLinkClick` takes over. Emitted plain (no CSP nonce), like all Thunderbolt
// inline scripts (see render-body.ejs).
export const buildPreventSamePageAnchorReloadScript = () =>
	`<script>;(function(){${isSamePageAnchor.toString()};var guard=(${preventSamePageAnchorReloadBeforeHydration.toString()});window.__tbAnchorGuard=guard;document.addEventListener('click',guard,true)})();</script>`

export const preHydrationAnchorGuardFactory = (
	headContent: IHeadContent,
	experiments: Experiments
): IAppWillMountHandler => ({
	appWillMount: () => {
		if (!isExperimentOpen(experiments, PREVENT_ANCHOR_RELOAD_SPEC)) {
			return
		}
		headContent.setHead(buildPreventSamePageAnchorReloadScript())
	},
})

export const PreHydrationAnchorGuard = withDependencies(
	[HeadContentSymbol, ExperimentsSymbol] as const,
	preHydrationAnchorGuardFactory
)
